import React from 'react';

import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';

import copies from '../../copies.json';
import SupplementaryInformationSection from '../SupplementaryInformationSection';

import AddSectionPanel from './AddSectionPanel';
import BaseAdditionalInformationSection from './BaseAdditionalInformationSection';
import CustomAdditionalInformationSection from './CustomAdditionalInformationSection';

const AUTO_SPECIFIC_FIELDS = [
  {
    id: 'police_and_fire_department',
    title: 'Police and Fire Department',
    fieldsIds: [
      'were_police_involved',
      'pending_prosecutions_or_investigations_exists',
      'police_crime_reference_number',
      'police_department_contact_id',
      'police_report',
      'did_speak_with_officer',
      'was_ticket_received',
      'violation_title',
      'police_description',
      'were_fire_department_involved',
      'fire_department_description',
      'were_any_hazardous_good_being_carried',
    ],
  },
  {
    id: 'theft_follow_up',
    title: 'Theft Follow Up',
    fieldsIds: [
      'discovered_the_loss_contact_id',
      'crime_reference_number',
      'date_noticed_vehicle_missing',
      'time_noticed_vehicle_missing',
      'date_vehicle_last_seen',
      'time_vehicle_last_seen',
      'vehicle_last_seen_location',
      'were_belongings_stolen_from_vehicle',
      'belongings_stolen',
      'is_vehicle_for_sale',
      'person_liable_for_the_theft_is_known',
      'note',
      'num_of_keys_received_on_purchase',
      'current_num_of_keys',
      'keys_location',
      'was_vehicle_found_with_keys',
      'vehicle_windows_closed_and_secured',
      'vehicle_fitted_with_security_devices',
      'security_devices_description',
      'vehicle_fitted_with_tracking_devices',
      'tracking_devices_description',
    ],
  },
  {
    id: 'fault_assessment',
    title: 'Fault Assessment',
    fieldsIds: [
      'first_party_fault_assessment',
      'claim_handler_fault_assessment',
      'is_indemnity_review_needed',
      'is_fraud_review_needed',
      'percentage_of_insured_liability',
      'liability_summary',
    ],
  },
  {
    id: 'accident_follow_up',
    title: 'Accident Follow Up',
    fieldsIds: [
      'journey_purpose',
      'weather_conditions',
      'direction_ph_vehicle_was_going',
      'first_party_way_sign_or_a_headlight',
      'street_condition',
      'was_ambulance_called',
      'was_first_party_driver_using_the_phone',
      'were_first_party_vehicle_lights_on',
      'was_anyone_under_the_influence_of_drugs_alcohol',
    ],
  },
];

// The four built-ins are column-backed and have no position in the `additional_information_sections`
// array, so the reorder body pins them in place exactly as it pins Supplementary Information.
const AUTO_SECTION_IDS = AUTO_SPECIFIC_FIELDS.map((section) => section.id);

interface Section {
  id: string;
  title: string;
  node?: JSX.Element;
  children?: { id: string; title: string; node?: JSX.Element; isHidden?: boolean }[];
  isCollapsible?: boolean;
  // Render the group in the main panel as a plain, non-collapsible titled band
  // (its children stacked inside a tinted container) instead of flattening them to top-level cards.
  renderBodyAsBand?: boolean;
  // Lays the band's children out itself (here: to add drag-reorder). Omitted, the band renders as before.
  renderBandBody?: (args: { children: Section[]; renderChild: (child: Section) => React.ReactNode }) => React.ReactNode;
}

// Minimal live-Formik slice the nav memoizes on — a section's identity + label.
interface CustomSectionNavItem {
  id: string;
  desc: string;
}

interface SectionsOptions {
  customSections?: CustomSectionNavItem[];
  isAddSectionEnabled?: boolean;
  // Supplied by the panel, which holds the Formik context the reorder writes to. Absent (a caller with
  // no form) means no drag affordance and the band renders as it always has.
  onReorderSections?: (reorder: { fromIndex: number; toIndex: number }) => void;
}

const SUPPLEMENTARY_ID = 'supplementary_information';
const ADD_SECTION_PANEL_ID = 'add_section_panel';

/** Drag-reorder over the custom sections only, with the fixed entries kept where they belong.
 *
 * Supplementary Information is a built-in and stays pinned first, any `pinnedIds` the caller names
 * (auto's four column-backed sections) come next, and the Add-Section panel is not a section at all and
 * stays last. None of them has a reorder meaning, and because they bracket the custom sections the
 * draggable block is contiguous. Indices are relative to that block, which is exactly the order of the
 * `additional_information_sections` array the handler writes to.
 */
const buildReorderableBandBody = (
  onReorderSections: (reorder: { fromIndex: number; toIndex: number }) => void,
  pinnedIds: readonly string[] = []
) =>
  function ReorderableBandBody({
    children,
    renderChild,
  }: {
    children: Section[];
    renderChild: (child: Section) => React.ReactNode;
  }) {
    const isFixed = (id: string) => id === SUPPLEMENTARY_ID || id === ADD_SECTION_PANEL_ID || pinnedIds.includes(id);
    const draggable = children.filter((child) => !isFixed(child.id));

    return (
      <>
        {children.filter((child) => child.id === SUPPLEMENTARY_ID).map(renderChild)}
        {children.filter((child) => pinnedIds.includes(child.id)).map(renderChild)}
        <DndOrderedList
          className="grid gap-16"
          items={draggable}
          getItemId={(child) => child.id}
          onReorder={onReorderSections}
          renderItem={(child) => renderChild(child)}
        />
        {children.filter((child) => child.id === ADD_SECTION_PANEL_ID).map(renderChild)}
      </>
    );
  };

const getSupplementaryInformationSection = (): Section[] => [
  {
    id: 'supplementary_information',
    title: 'Supplementary Information',
    node: <SupplementaryInformationSection />,
  },
];

type SectionChild = NonNullable<Section['children']>[number];

const getCustomSections = (customSections: CustomSectionNavItem[]): SectionChild[] =>
  customSections.map((section, index) => ({
    id: section.id,
    title: section.desc || section.id,
    node: <CustomAdditionalInformationSection index={index} />,
  }));

// The Add-Section affordance renders in the group body but is not a nav entry.
const getAddSectionPanel = (isAddSectionEnabled: boolean): SectionChild[] =>
  isAddSectionEnabled ? [{ id: ADD_SECTION_PANEL_ID, title: '', node: <AddSectionPanel />, isHidden: true }] : [];

const getAutoSpecificSections = ({
  customSections = [],
  isAddSectionEnabled = false,
  onReorderSections,
}: SectionsOptions): Section[] => [
  {
    id: 'additional_information',
    title: 'Additional Information',
    // The sidebar nav entry stays collapsible; the main-panel body renders as a non-collapsible band.
    isCollapsible: true,
    renderBodyAsBand: true,
    // Only worth a drag affordance once there is more than one custom section to move; the built-ins
    // are pinned, so they never enter the draggable block.
    renderBandBody:
      onReorderSections && customSections.length > 1
        ? buildReorderableBandBody(onReorderSections, AUTO_SECTION_IDS)
        : undefined,
    children: [
      ...getSupplementaryInformationSection(),
      ...AUTO_SPECIFIC_FIELDS.map((section) => ({
        id: section.id,
        title: section.title,
        node: (
          <BaseAdditionalInformationSection
            title={section.title}
            sectionKey={section.id}
            sectionKeyPrefix="auto_incident_additional"
            fieldIds={section.fieldsIds}
          />
        ),
      })),
      ...getCustomSections(customSections),
      ...getAddSectionPanel(isAddSectionEnabled),
    ],
  },
];

const getNonAutoSpecificSections = ({
  customSections = [],
  isAddSectionEnabled = false,
  onReorderSections,
}: SectionsOptions): Section[] => [
  {
    id: 'additional_information',
    title: copies.additional_information.group_title,
    // The sidebar nav entry stays collapsible (FsIndexPanel keys that on non-empty children); the
    // main-panel body renders as a non-collapsible band.
    renderBodyAsBand: true,
    // Only worth a drag affordance once there is more than one section to move.
    renderBandBody:
      onReorderSections && customSections.length > 1 ? buildReorderableBandBody(onReorderSections) : undefined,
    children: [
      ...getSupplementaryInformationSection(),
      ...getCustomSections(customSections),
      ...getAddSectionPanel(isAddSectionEnabled),
    ],
  },
];

const getLobSpecificAdditionalInfoSections = (lob: string, options: SectionsOptions = {}): Section[] =>
  lob === 'auto_claim' ? getAutoSpecificSections(options) : getNonAutoSpecificSections(options);

export { getLobSpecificAdditionalInfoSections };
